'use client'

/**
 * Wires every `.railnav` prev/next pair in the generated sections to the
 * NEAREST FOLLOWING `.rail` — the artboard's own rule, so a section with two
 * rails (cards + colors) gets two working pairs. Re-runs whenever the page's
 * DOM changes (tab panels re-create rails).
 *
 * FRIDGE-875: `root` names the subtree to wire, defaulting to the homepage's
 * own `main.hp26`. The collection merchandising rails reuse the same markup
 * inside a `<section class="hp26">` on a page that already has its own
 * `<main>`, so they pass their own selector rather than the binder growing a
 * second copy of this logic. The homepage call passes nothing and behaves
 * exactly as before.
 */
import * as React from 'react'

function nearestRail(nav: Element): HTMLElement | null {
  let node: Element | null = nav
  while (node && node !== document.body) {
    let sib = node.nextElementSibling
    while (sib) {
      if (sib.classList.contains('rail')) return sib as HTMLElement
      const inner = sib.querySelector<HTMLElement>('.rail')
      if (inner) return inner
      sib = sib.nextElementSibling
    }
    node = node.parentElement
  }
  return null
}

export function RailNavBinder({ root = 'main.hp26' }: { root?: string } = {}) {
  React.useEffect(() => {
    const main = document.querySelector(root)
    if (!main) return
    const cleanups: Array<() => void> = []
    const wire = () => {
      main.querySelectorAll<HTMLElement>('.railnav').forEach((nav) => {
        if (nav.dataset.wired) return
        const rail = nearestRail(nav)
        if (!rail) return
        nav.dataset.wired = '1'
        const btns = nav.querySelectorAll('button')
        const previous = () => rail.scrollBy({ left: -rail.clientWidth * 0.8, behavior: 'smooth' })
        const next = () => rail.scrollBy({ left: rail.clientWidth * 0.8, behavior: 'smooth' })
        btns[0]?.addEventListener('click', previous)
        btns[1]?.addEventListener('click', next)
        cleanups.push(() => {
          btns[0]?.removeEventListener('click', previous)
          btns[1]?.removeEventListener('click', next)
          delete nav.dataset.wired
        })
        // Optional position indicator: native scrolling stays in charge on touch.
        const progress = rail.nextElementSibling
        if (progress instanceof HTMLProgressElement) {
          const update = () => {
            progress.max = Math.max(1, rail.scrollWidth)
            progress.value = Math.min(rail.scrollWidth, Math.max(0, rail.scrollLeft) + rail.clientWidth)
          }
          update()
          rail.addEventListener('scroll', update, { passive: true })
          const resize = new ResizeObserver(update)
          resize.observe(rail)
          cleanups.push(() => {
            rail.removeEventListener('scroll', update)
            resize.disconnect()
          })
        }
      })
    }
    wire()
    const mo = new MutationObserver(() => wire())
    mo.observe(main, { childList: true, subtree: true })
    return () => {
      mo.disconnect()
      cleanups.forEach(cleanup => cleanup())
    }
  }, [root])
  return null
}
